import type jsPDF from 'jspdf';

import InterBoldTTFUrl from '@assets/fonts/inter/Inter-Bold.ttf';
import InterRegularTTFUrl from '@assets/fonts/inter/Inter-Regular.ttf';
import QBadgePngUrl from '@assets/PNGs/QontactPoints/Q-badge.png';
import QontactDarkSvgUrl from '@assets/SVGs/GeneralUse/Qontact-dark.svg';
import { buildQRSheetFileName, QRSheetType } from '@utils/QRSheetFileName';

export type { QRSheetType } from '@utils/QRSheetFileName';

export interface BuildQRSheetPdfParams {
    qrType: QRSheetType;
    pointName: string;
    // High-res level "H" QR bitmap (see svgToPngDataUrl in QontactPointDetailModalViewModel).
    // Level H is required because the Q badge overlay covers the QR center.
    qrPngDataUrl: string;
}

// ---- Print constants -------------------------------------------------
// These are values from the approved A4 print-sheet design, not app design
// system tokens (the app's `designSystemStyles` govern on-screen UI only).
const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const SIDE_PADDING_MM = 18;
const BLOCK_GAP_MM = 11;

const COLOR_BACKGROUND = '#FBFAF6';
const COLOR_TEXT_PRIMARY = '#300E00';
const COLOR_TEXT_SECONDARY = '#602400';
const COLOR_ACCENT = '#FF783A';
const COLOR_WHITE = '#FFFFFF';
const COLOR_CARD_SHADOW = '#ECE5D8';

// QR modules are printed in the brand's dark text color rather than pure
// black. Exported so the hidden, high-res level-H QRCode used for export
// (rendered by the screen/view model) can match it exactly.
export const QR_SHEET_FOREGROUND_COLOR = COLOR_TEXT_PRIMARY;

const CARD_SIZE_MM = 124;
const CARD_RADIUS_MM = 4;
const CARD_SHADOW_OFFSET_MM = 1.4;
const QR_SIZE_MM = 108;

const BADGE_TILE_SIZE_MM = 18;
const BADGE_TILE_RADIUS_MM = 2.1;
const BADGE_IMAGE_SIZE_MM = 14.5;

const FOOTER_LOGO_HEIGHT_MM = 5.5;
const FOOTER_MARGIN_BOTTOM_MM = 12;
// Qontact-dark.svg viewBox is 201x38.
const FOOTER_LOGO_ASPECT_RATIO = 201 / 38;
const FOOTER_LOGO_RASTER_HEIGHT_PX = 220;

const HEADING_LINE1_FONT_SIZE_PT = 20;
const HEADING_LINE2_FONT_SIZE_PT = 30;
const HEADING_LINE2_CHAR_SPACE_MM = -0.25;
const HEADING_LINE_GAP_MM = 2;

const POINT_NAME_FONT_SIZE_PT = 22;
const POINT_NAME_LABEL_FONT_SIZE_PT = 12.5;
const POINT_NAME_LABEL_GAP_MM = 1.5;

const FONT_FAMILY = 'Inter';
const LINE_HEIGHT_FACTOR = 1.15;
const PT_TO_MM = 25.4 / 72;
const ptToMm = (pt: number): number => pt * PT_TO_MM;
const lineHeightMm = (fontSizePt: number): number => ptToMm(fontSizePt * LINE_HEIGHT_FACTOR);

// Built lazily (not as a module-level constant) so `.localized()` — a
// String.prototype polyfill registered at app bootstrap — is guaranteed to
// be available by the time this runs (a user download click, well after
// bootstrap), rather than at module import time.
const getHeading = (qrType: QRSheetType): { line1: string; line2: string } => {
    switch (qrType) {
        case 'whatsapp':
            return { line1: 'QR de'.localized(), line2: 'WhatsApp' };
        case 'app':
            return { line1: 'Escaneá con'.localized(), line2: 'el celular'.localized() };
    }
};

const getPointNameLabel = (): string => 'Qontact Point'.localized();

// ---- Asset loading helpers ---------------------------------------------

const fetchAsDataUrl = async (url: string): Promise<string> => {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error(`No se pudo leer el asset: ${url}`));
        reader.readAsDataURL(blob);
    });
};

const fetchAsBase64 = async (url: string): Promise<string> => {
    const dataUrl = await fetchAsDataUrl(url);
    const commaIndex = dataUrl.indexOf(',');
    return commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
};

// Same canvas rasterization technique as svgToPngDataUrl in
// QontactPointDetailModalViewModel, but for a static SVG asset (fetched by
// URL) instead of a live DOM SVGElement.
const rasterizeSvgAssetToPngDataUrl = async (
    svgUrl: string,
    widthPx: number,
    heightPx: number
): Promise<string> => {
    const svgText = await (await fetch(svgUrl)).text();
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const blobUrl = URL.createObjectURL(svgBlob);

    return new Promise<string>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = widthPx;
            canvas.height = heightPx;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                URL.revokeObjectURL(blobUrl);
                reject(new Error('No se pudo crear el contexto de canvas'));
                return;
            }

            ctx.drawImage(img, 0, 0, widthPx, heightPx);
            URL.revokeObjectURL(blobUrl);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => {
            URL.revokeObjectURL(blobUrl);
            reject(new Error(`No se pudo cargar el SVG: ${svgUrl}`));
        };
        img.src = blobUrl;
    });
};

const registerInterFont = async (doc: jsPDF): Promise<void> => {
    const [regularBase64, boldBase64] = await Promise.all([
        fetchAsBase64(InterRegularTTFUrl),
        fetchAsBase64(InterBoldTTFUrl),
    ]);

    doc.addFileToVFS('Inter-Regular.ttf', regularBase64);
    doc.addFont('Inter-Regular.ttf', FONT_FAMILY, 'normal');
    doc.addFileToVFS('Inter-Bold.ttf', boldBase64);
    doc.addFont('Inter-Bold.ttf', FONT_FAMILY, 'bold');
};

// ---- PDF builder ---------------------------------------------------------

export const buildQRSheetPdf = async ({
    qrType,
    pointName,
    qrPngDataUrl,
}: BuildQRSheetPdfParams): Promise<void> => {
    const [{ default: jsPDFCtor }, qBadgeDataUrl, footerLogoDataUrl] = await Promise.all([
        import('jspdf'),
        fetchAsDataUrl(QBadgePngUrl),
        rasterizeSvgAssetToPngDataUrl(
            QontactDarkSvgUrl,
            Math.round(FOOTER_LOGO_RASTER_HEIGHT_PX * FOOTER_LOGO_ASPECT_RATIO),
            FOOTER_LOGO_RASTER_HEIGHT_PX
        ),
    ]);

    const doc = new jsPDFCtor({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    await registerInterFont(doc);

    // Full-bleed background.
    doc.setFillColor(COLOR_BACKGROUND);
    doc.rect(0, 0, PAGE_WIDTH_MM, PAGE_HEIGHT_MM, 'F');

    const centerX = PAGE_WIDTH_MM / 2;
    const contentWidth = PAGE_WIDTH_MM - SIDE_PADDING_MM * 2;

    const footerLogoWidthMm = FOOTER_LOGO_HEIGHT_MM * FOOTER_LOGO_ASPECT_RATIO;
    const footerTopY = PAGE_HEIGHT_MM - FOOTER_MARGIN_BOTTOM_MM - FOOTER_LOGO_HEIGHT_MM;

    // Point name may wrap, so measure it before computing vertical centering.
    doc.setFont(FONT_FAMILY, 'bold');
    doc.setFontSize(POINT_NAME_FONT_SIZE_PT);
    const pointNameLines = doc.splitTextToSize(pointName, contentWidth) as string[];

    const headingBlockHeight =
        lineHeightMm(HEADING_LINE1_FONT_SIZE_PT) + HEADING_LINE_GAP_MM + lineHeightMm(HEADING_LINE2_FONT_SIZE_PT);
    const pointNameBlockHeight =
        pointNameLines.length * lineHeightMm(POINT_NAME_FONT_SIZE_PT) +
        POINT_NAME_LABEL_GAP_MM +
        lineHeightMm(POINT_NAME_LABEL_FONT_SIZE_PT);

    const totalContentHeight =
        headingBlockHeight + BLOCK_GAP_MM + CARD_SIZE_MM + BLOCK_GAP_MM + pointNameBlockHeight;

    let cursorY = Math.max((footerTopY - totalContentHeight) / 2, SIDE_PADDING_MM);

    // -- Heading --
    const heading = getHeading(qrType);

    doc.setFont(FONT_FAMILY, 'normal');
    doc.setFontSize(HEADING_LINE1_FONT_SIZE_PT);
    doc.setTextColor(COLOR_TEXT_PRIMARY);
    doc.text(heading.line1, centerX, cursorY, { align: 'center', baseline: 'top' });
    cursorY += lineHeightMm(HEADING_LINE1_FONT_SIZE_PT) + HEADING_LINE_GAP_MM;

    doc.setFont(FONT_FAMILY, 'bold');
    doc.setFontSize(HEADING_LINE2_FONT_SIZE_PT);
    doc.setTextColor(COLOR_ACCENT);
    doc.text(heading.line2, centerX, cursorY, {
        align: 'center',
        baseline: 'top',
        charSpace: HEADING_LINE2_CHAR_SPACE_MM,
    });
    cursorY += lineHeightMm(HEADING_LINE2_FONT_SIZE_PT) + BLOCK_GAP_MM;

    // -- White card + QR + badge --
    const cardX = centerX - CARD_SIZE_MM / 2;
    const cardY = cursorY;

    doc.setFillColor(COLOR_CARD_SHADOW);
    doc.roundedRect(
        cardX + CARD_SHADOW_OFFSET_MM,
        cardY + CARD_SHADOW_OFFSET_MM,
        CARD_SIZE_MM,
        CARD_SIZE_MM,
        CARD_RADIUS_MM,
        CARD_RADIUS_MM,
        'F'
    );

    doc.setFillColor(COLOR_WHITE);
    doc.roundedRect(cardX, cardY, CARD_SIZE_MM, CARD_SIZE_MM, CARD_RADIUS_MM, CARD_RADIUS_MM, 'F');

    const qrX = cardX + (CARD_SIZE_MM - QR_SIZE_MM) / 2;
    const qrY = cardY + (CARD_SIZE_MM - QR_SIZE_MM) / 2;
    doc.addImage(qrPngDataUrl, 'PNG', qrX, qrY, QR_SIZE_MM, QR_SIZE_MM);

    const cardCenterY = cardY + CARD_SIZE_MM / 2;
    const badgeTileX = centerX - BADGE_TILE_SIZE_MM / 2;
    const badgeTileY = cardCenterY - BADGE_TILE_SIZE_MM / 2;

    doc.setFillColor(COLOR_WHITE);
    doc.roundedRect(
        badgeTileX,
        badgeTileY,
        BADGE_TILE_SIZE_MM,
        BADGE_TILE_SIZE_MM,
        BADGE_TILE_RADIUS_MM,
        BADGE_TILE_RADIUS_MM,
        'F'
    );

    const badgeImageX = centerX - BADGE_IMAGE_SIZE_MM / 2;
    const badgeImageY = cardCenterY - BADGE_IMAGE_SIZE_MM / 2;
    doc.addImage(qBadgeDataUrl, 'PNG', badgeImageX, badgeImageY, BADGE_IMAGE_SIZE_MM, BADGE_IMAGE_SIZE_MM);

    cursorY = cardY + CARD_SIZE_MM + BLOCK_GAP_MM;

    // -- Point name block --
    doc.setFont(FONT_FAMILY, 'bold');
    doc.setFontSize(POINT_NAME_FONT_SIZE_PT);
    doc.setTextColor(COLOR_TEXT_PRIMARY);
    doc.text(pointNameLines, centerX, cursorY, { align: 'center', baseline: 'top' });
    cursorY += pointNameLines.length * lineHeightMm(POINT_NAME_FONT_SIZE_PT) + POINT_NAME_LABEL_GAP_MM;

    doc.setFont(FONT_FAMILY, 'normal');
    doc.setFontSize(POINT_NAME_LABEL_FONT_SIZE_PT);
    doc.setTextColor(COLOR_TEXT_SECONDARY);
    doc.text(getPointNameLabel(), centerX, cursorY, { align: 'center', baseline: 'top' });

    // -- Footer wordmark --
    doc.addImage(
        footerLogoDataUrl,
        'PNG',
        centerX - footerLogoWidthMm / 2,
        footerTopY,
        footerLogoWidthMm,
        FOOTER_LOGO_HEIGHT_MM
    );

    doc.save(buildQRSheetFileName(qrType, pointName));
};
